import { FunctionComponent, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import ErrorMessage from '../../common/error/ErrorMessage';
import { addDays, getStartOfWeek, tryFormatDate } from '../../common/utils/dateUtils';
import { useAsset, useAssetBookings } from '../assets/assetService';
import BookingModal from '../bookingModal/BookingModal';
import PaperLayout from '../layout/PaperLayout';
import styles from './BookingOverview.module.scss';
import DateSelector from './components/DateSelector';
import DetailedSchedule from './components/DetailedSchedule';
import ObjectSelector from './components/ObjectSelector';

const BookingOverview: FunctionComponent = () => {
    const { id } = useParams() as any;
    const [selectedBookingId, setSelectedBookingId] = useState<string | undefined>();
    const [objectFilter, setObjectFilter] = useState<string[]>([]);
    const [startDate, setStartDate] = useState<Date>(getStartOfWeek(new Date()));

    const from = useMemo(() => tryFormatDate(startDate), [startDate]);
    const to = useMemo(() => tryFormatDate(addDays(startDate, 7)), [startDate]);

    const { asset, error: assetError } = useAsset(id);
    const { bookings, isLoading, error: bookingsError, refetch } = useAssetBookings(id, from, to);

    const error = assetError || bookingsError;

    if (error) {
        return <ErrorMessage message="Kunde inte hämta resursinformation eller bokningar" />;
    }

    if (!asset || !bookings) {
        return null;
    }

    return (
        <PaperLayout heading="Bokningsöversikt" tightenUp={true}>
            <Link to=".." className="paper__back">
                Tillbaka till resursen
            </Link>

            <div className={styles.preamble}>
                Visar alla bokningar under den valda veckan. Klicka på en bokning för att komma till detaljvyn. Vill du inspirera fler boende att använda resursen? Använd gärna våra framtagna mallar
                för utdelning till boende och anslag i trapphuset. &nbsp;
                <a href="https://mitt.riksbyggen.se/styrelsearbete/foreningen/hemshare/" target="_blank" rel="noreferrer">
                    Läs mer här
                </a>
            </div>

            <DateSelector startDate={startDate} setStartDate={setStartDate} />

            {asset.objects.length > 1 && <ObjectSelector objects={asset.objects} objectFilter={objectFilter} setObjectFilter={setObjectFilter} />}

            <DetailedSchedule asset={asset} bookings={bookings} objectFilter={objectFilter} startDate={startDate} setSelectedBookingId={setSelectedBookingId} />

            {selectedBookingId && <BookingModal bookingId={selectedBookingId} hasMultipleObjects={asset.objects?.length > 1} onClose={() => setSelectedBookingId(undefined)} onRefresh={refetch} />}

            {isLoading && <div className={styles.blocker}></div>}
        </PaperLayout>
    );
};

export default BookingOverview;
